import simplepayLogo from '../assets/payments/simplepay.png';
import barionBanner from '../assets/payments/barion-smart-banner-light.svg';
import paypalLogo from '../assets/payments/paypal-mark.png';
import stripeLogo from '../assets/payments/stripe-badge.svg';

export type PaymentOutcome = 'succeeded' | 'failed' | 'cancelled' | 'timeout' | 'pending';

export interface ProviderCompliance {
  /**
   * Mark rendered inside the provider's payment-option card. `wide` marks a
   * banner-shaped strip that should span the card's width (undistorted, height
   * follows) instead of the fixed small-mark height.
   */
  logo?: { src: string; alt: string; wide?: boolean };
  /**
   * Provider-mandated logo strip rendered standalone, in close proximity to the
   * payment methods (payment step, review/place-order page, footer) — see
   * `PaymentProviderBanner`.
   */
  banner?: { src: string; alt: string };
  infoLink?: (locale: string) => string;
  requiresDataTransferDeclaration: boolean;
  declarationKey?: string; // i18n key, vars: {{company}}, {{origin}}, {{data}} — company already includes "(seat)" when set
  result?: Partial<Record<PaymentOutcome, { textKey: string; showProviderRef: boolean }>>;
}

const SIMPLEPAY: ProviderCompliance = {
  logo: { src: simplepayLogo, alt: 'SimplePay - Online Bank Card Payment' },
  infoLink: (locale) =>
    locale === 'hu'
      ? 'https://simplepartner.hu/PaymentService/Fizetesi_tajekoztato.pdf'
      : 'https://simplepartner.hu/PaymentService/Payment_information.pdf',
  requiresDataTransferDeclaration: true,
  declarationKey: 'payment_compliance.simplepay.declaration',
  result: {
    succeeded: { textKey: 'payment_compliance.simplepay.result_succeeded', showProviderRef: true },
    failed: { textKey: 'payment_compliance.simplepay.result_failed', showProviderRef: true },
    cancelled: { textKey: 'payment_compliance.simplepay.result_cancelled', showProviderRef: false },
    timeout: { textKey: 'payment_compliance.simplepay.result_timeout', showProviderRef: false },
    pending: { textKey: 'payment_compliance.simplepay.result_pending', showProviderRef: true },
  },
};

// Barion/PayPal/Stripe: display-only entries (no data-transfer declaration —
// none of these providers required the SimplePay-style disclosure at signup
// time). Assets are unmodified official artwork:
//  - barion-smart-banner-light.svg: Barion's "Smart Payment Banner" (Oct 2025,
//    light-mode SVG) from the package linked on Barion's help article about
//    displaying the accepted payment-method logos —
//    https://download.barion.com/barion/barion-smart-payment-banner-EU.zip.
//    Copied byte-for-byte; Barion requires the strip unmodified on the shop's
//    main page and payment page(s) as a prerequisite of live-shop approval,
//    and the package's Dev guide adds: keep the aspect ratio, no borders/
//    shadows/recolouring, ≥8px clearance, legible at every breakpoint, light
//    variant on light backgrounds (our footer/checkout backgrounds are light).
//    Replaced the pre-2025 card strips (Maestro/Amex/Visa Electron) that
//    Barion flagged as outdated in Sept 2026.
//  - paypal-mark.png: official paypalobjects.com CDN
//  - stripe-badge.svg: official "Powered by Stripe" badge zip linked from
//    stripe.com/newsroom/brand-assets (assets.stripeassets.com)
const BARION_BANNER = { src: barionBanner, alt: 'Barion, Mastercard, Visa, Apple Pay, Google Pay' };
const BARION: ProviderCompliance = {
  // Same strip in the option card (spanning its width) and standalone below the options.
  logo: { ...BARION_BANNER, wide: true },
  banner: BARION_BANNER,
  infoLink: (locale) => (locale === 'hu' ? 'https://www.barion.com/hu/' : 'https://www.barion.com/en/'),
  requiresDataTransferDeclaration: false,
};

const PAYPAL: ProviderCompliance = {
  logo: { src: paypalLogo, alt: 'PayPal' },
  infoLink: () => 'https://www.paypal.com/',
  requiresDataTransferDeclaration: false,
};

const STRIPE: ProviderCompliance = {
  logo: { src: stripeLogo, alt: 'Powered by Stripe' },
  infoLink: () => 'https://stripe.com/',
  requiresDataTransferDeclaration: false,
};

const PROVIDER_COMPLIANCE: Record<string, ProviderCompliance> = {
  simplepay: SIMPLEPAY,
  barion: BARION,
  paypal: PAYPAL,
  stripe: STRIPE,
};

export const getProviderCompliance = (techid: string | null | undefined): ProviderCompliance | undefined =>
  (techid && PROVIDER_COMPLIANCE[techid]) || undefined;
